import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ProposalForm } from '../components/ProposalForm';
import { useCreateProposal, useProposal, useUpdateProposal } from '../queries';
import type { ProposalInput } from '../types';

export function ProposalFormPage() {
  const { publicId = '' } = useParams();
  const isEdit = Boolean(publicId);
  const proposalId = publicId;
  const navigate = useNavigate();

  const proposalQuery = useProposal(isEdit ? proposalId : '');
  const create = useCreateProposal();
  const update = useUpdateProposal(proposalId);

  async function handleSubmit(values: ProposalInput) {
    const saved = isEdit ? await update.mutateAsync(values) : await create.mutateAsync(values);
    navigate(`/crm/proposals/${saved.public_id}`);
  }

  if (isEdit && proposalQuery.isPending) {
    return (
      <div className="max-w-xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  const proposal = proposalQuery.data;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(isEdit ? `/crm/proposals/${proposalId}` : '/crm/proposals')}
        >
          <ArrowLeft />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">
          {isEdit ? `Edit ${proposal?.title ?? 'proposal'}` : 'New proposal'}
        </h1>
      </div>

      <ProposalForm
        submitLabel={isEdit ? 'Save changes' : 'Create proposal'}
        defaultValues={
          proposal
            ? {
                title: proposal.title,
                company: String(proposal.company),
                deal: proposal.deal ? String(proposal.deal) : '',
                status: proposal.status,
                currency: proposal.currency,
                notes: proposal.notes,
              }
            : undefined
        }
        onSubmit={handleSubmit}
        onCancel={() => navigate(isEdit ? `/crm/proposals/${proposalId}` : '/crm/proposals')}
      />
    </div>
  );
}
